import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Home, Compass, Heart } from 'lucide-react';
import { colors } from '../styles/colors';
import { spacing } from '../styles/spacing';
import { typography } from '../styles/typography';

const NavContainer = styled(motion.div)`
  position: fixed;
  bottom: ${spacing[4]};
  left: 0;
  right: 0;
  width: auto;
  max-width: 500px;
  margin: 0 auto;
  height: 72px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${spacing.radius.full};
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0 ${spacing[2]};
  z-index: ${spacing.zIndex.docked};
`;

const NavItemContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const NavItem = styled.button<{ $isActive: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.xs};
  font-weight: ${typography.weights.medium};
  color: ${props => props.$isActive ? colors.text.primary : colors.text.muted};
  transition: color 0.2s ease-in-out, transform 0.2s ease-in-out;
  position: relative;
  height: 100%;

  &:hover {
    color: ${colors.text.primary};
  }
`;

const NavIconWrapper = styled.div`
  position: relative;
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${colors.primary.DEFAULT};
`;

const AddButton = styled.button`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${colors.gradients.primary};
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  color: ${colors.text.white};
  font-family: ${typography.fonts.primary};
  font-size: ${typography.sizes.xs};
  font-weight: ${typography.weights.medium};
  box-shadow: ${colors.shadows.md};
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${colors.shadows.lg};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const AddButtonContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
`;

const SparkleInstance = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #FFD700;
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

const Sparkles = ({ onComplete }: { onComplete: () => void }) => {
  const sparkles = Array.from({ length: 8 });
  const radius = 45;

  return (
    <>
      {sparkles.map((_, i) => {
        const angle = (i / sparkles.length) * 360 * (Math.PI / 180);
        const x = radius * Math.cos(angle);
        const y = radius * Math.sin(angle);

        return (
          <SparkleInstance
            key={i}
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{
              x,
              y,
              scale: [0.5, 1, 0],
              opacity: [1, 0.5, 0],
            }}
            transition={{
              duration: 0.6,
              ease: 'circOut',
            }}
            onAnimationComplete={i === 0 ? onComplete : undefined}
          />
        );
      })}
    </>
  );
};

type NavItemType = 'home' | 'discover' | 'messages' | 'profile';

interface BottomNavBarProps {
  activeItem: NavItemType;
  onItemClick: (item: NavItemType) => void;
  onAddClick: () => void;
}

const BottomNavBar: React.FC<BottomNavBarProps> = ({ activeItem, onItemClick, onAddClick }) => {
  const [isSparkling, setIsSparkling] = useState(false);

  const handlePingClick = () => {
    onAddClick();
    setIsSparkling(true);
  };

  return (
    <NavContainer
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20, delay: 0.5 }}
    >
      <NavItemContainer>
        <NavItem key="home" $isActive={activeItem === 'home'} onClick={() => onItemClick('home')}>
          <NavIconWrapper>
            <Home size={24} strokeWidth={activeItem === 'home' ? 2.5 : 2} />
          </NavIconWrapper>
          <span>Home</span>
          {activeItem === 'home' && <ActiveIndicator layoutId="activeIndicator" />}
        </NavItem>
      </NavItemContainer>
      <AddButton onClick={handlePingClick}>
        {isSparkling && <Sparkles onComplete={() => setIsSparkling(false)} />}
        <AddButtonContent>
          <Heart size={20} fill="#FFD700" stroke="#FFD700" />
          <span>Ping!</span>
        </AddButtonContent>
      </AddButton>
      <NavItemContainer>
        <NavItem key="discover" $isActive={activeItem === 'discover'} onClick={() => onItemClick('discover')}>
          <NavIconWrapper>
            <Compass size={24} strokeWidth={activeItem === 'discover' ? 2.5 : 2} />
          </NavIconWrapper>
          <span>Discover</span>
          {activeItem === 'discover' && <ActiveIndicator layoutId="activeIndicator" />}
        </NavItem>
      </NavItemContainer>
    </NavContainer>
  );
};

export default BottomNavBar;
